import React, { useState, useEffect } from "react";
import { formatAppDate } from "../../helpers/date-formatter";
import { Link } from "react-router-dom";
import { ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import DataTable from "../table/DataTable";
import { TextStack } from "../table/DataTableCell";
import StatusBadge from "../table/StatusBadge";

function InvoiceRedeemTable({
  offset,
  data,
  path,
  sort,
  onSort,
  onToggleApprove,
}) {
  const [sortingIndex, setSortingIndex] = useState(null);

  useEffect(() => {
    if (sort === null) {
      setSortingIndex(null);
    }
  }, [sort]);

  const handleSorting = (key, index) => {
    setSortingIndex(index);

    if (key === "user") {
      onSort("user.username");
    } else if (key === "amount (€)") {
      onSort("receivableAmount");
    } else if (key === "redeem date") {
      onSort("redeemedOn");
    } else if (key === "approved") {
      onSort("approved");
    } else if (key === "approved date") {
      onSort("approvedOn");
    } else if (key === "expiry date") {
      onSort("expiryDate");
    }
  };

  const sortableHeader = (label, key, index) => (
    <button
      type="button"
      className={`inline-flex items-center gap-2 font-semibold ${
        index === sortingIndex ? "text-primary" : "text-slate-700"
      } hover:text-primary-dark`}
      onClick={handleSorting.bind(null, key, index)}
    >
      {label}
      <ArrowsUpDownIcon className="h-4 w-4" />
    </button>
  );

  const columns = [
    {
      key: "sn",
      label: "SN",
      width: "90px",
      render: (_, key) => (offset && !offset.isNaN ? offset + key + 1 : key + 1),
    },
    {
      key: "user",
      label: "User",
      header: sortableHeader("User", "user", 1),
      render: (item) => (
        <TextStack
          primary={item.fullname}
          secondary={
            <Link to={`/users/${item.userId}`} className="text-icon-blue underline">
              {item.username}
            </Link>
          }
        />
      ),
    },
    {
      key: "amount",
      label: "Amount (€)",
      header: sortableHeader("Amount (€)", "amount (€)", 2),
      align: "center",
      width: "140px",
      render: (item) => `€${item.amount}`,
    },
    {
      key: "code",
      label: "Code",
      align: "center",
      width: "140px",
    },
    {
      key: "redeemedOn",
      label: "Redeem Date",
      header: sortableHeader("Redeem Date", "redeem date", 4),
      align: "center",
      width: "160px",
      render: (item) => (item.redeemedOn !== null ? formatAppDate(item.redeemedOn) : "-"),
    },
    {
      key: "approved",
      label: "Approved",
      header: sortableHeader("Approved", "approved", 5),
      align: "center",
      width: "150px",
      render: (item) => (
        <button
          type="button"
          disabled={item.approved}
          title={item.approved ? "Approved" : "Click to approve"}
          onClick={() => {
            if (!item.approved) {
              onToggleApprove(item.id);
            }
          }}
        >
          <StatusBadge
            active={item.approved}
            label={item.approved ? "Approved" : "Pending"}
            variant={item.approved ? "approved" : "pending"}
          />
        </button>
      ),
    },
    {
      key: "approvedDate",
      label: "Approved Date",
      header: sortableHeader("Approved Date", "approved date", 6),
      align: "center",
      width: "170px",
      render: (item) => (item.approvedDate !== null ? formatAppDate(item.approvedDate) : "-"),
    },
    {
      key: "expiryDate",
      label: "Expiry Date",
      header: sortableHeader("Expiry Date", "expiry date", 7),
      align: "center",
      width: "160px",
      render: (item) => (item.expiryDate !== null ? formatAppDate(item.expiryDate) : "-"),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data}
      rowKey="id"
      minWidth="1120px"
      emptyMessage="No redeems found."
    />
  );
}

export default InvoiceRedeemTable;
